import type { ReadonlySignal } from '@preact/signals-react';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { ReactContainer } from 'src/ReactContainer';
import type { WidgetLocation } from 'src/types/WidgetLocation';
import { z } from 'zod';

export const widgetIdSchema = z.string().brand<'WidgetId'>();
export type WidgetId = z.infer<typeof widgetIdSchema>;

export const containerIdSchema = z.string().brand<'ContainerId'>();
export type ContainerId = z.infer<typeof containerIdSchema>;

export const windowIdSchema = z.string().brand<'WindowId'>();
export type WindowId = z.infer<typeof windowIdSchema>;

export type GetInitialData<T> = () => Promise<T>;

export interface ContainerTabResolver {
    getInitialData(tabId: WidgetId): Promise<unknown>;
}

export interface SelfOwnedWidget<TState> {
    toJSON(): TState;
    readonly dirty: ReadonlySignal<boolean>;
    markClean(): void;
}

export interface SelfOwnedWidgetClass<TState, TModel extends SelfOwnedWidget<TState>> {
    readonly stateSchema: z.ZodType<TState>;
    readonly initialStateSchema?: z.ZodType<unknown>;
    defaultState(): TState;
    defaultInitialState?(): unknown;
    fromJSON(container: ReactContainer): Promise<TModel>;
}

export interface LedgerEntry<TState> {
    id: WidgetId;
    type: WidgetType;
    location: WidgetLocation;
    windowId: WindowId;
    persistedState: TState;
}

// `any` is load-bearing: `SelfOwnedWidgetClass<unknown, ...>` rejects concrete
// widget classes because their `stateSchema` types are invariant.
export type AnySelfOwnedWidgetClass = SelfOwnedWidgetClass<any, SelfOwnedWidget<any>>;
